import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Binary, RefreshCw, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Input, Label, Select } from '@/components/ui/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { getApiErrorMessage } from '@/lib/api-error';
import { apiClient } from '@/lib/api-client';
import {
  ENRICHMENT_PROVIDERS,
  type EnrichmentProviderId,
} from '@/lib/enrichment-providers';

type VerifyMode = 'basic' | 'enhanced' | 'comprehensive';

interface EmailSleuthStatusResponse {
  service: string;
  configured: {
    base_url: boolean;
    api_key: boolean;
  };
  connected: boolean;
  message: string | null;
  health: {
    status: string | null;
    service: string | null;
    auth_configured: boolean | null;
    timestamp: string | null;
  } | null;
}

interface EmailSleuthVerifyResult {
  name: string;
  domain: string;
  email: string | null;
  confidence: number | null;
  verified: boolean;
  method: string | null;
  candidates: Array<{
    email: string;
    score: number;
    verified: boolean;
    source: string;
    message: string | null;
  }>;
  cached: boolean;
  duration_ms: number;
  debug?: {
    methods_used?: string[];
    most_likely_email?: string | null;
  };
}

const EMAILSLEUTH_BASE_URL_EXAMPLE = 'http://localhost:8080';

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatConfidence(value: number | null): string {
  if (value === null) return '-';
  return `${Math.round(value * 100)}%`;
}

function getProviderActivationPath(providerId: EnrichmentProviderId): string {
  switch (providerId) {
    case 'csv':
      return 'Wizard campagne -> Enrichisseurs -> Import CSV';
    case 'leadscrape':
      return 'Wizard campagne -> Enrichisseurs -> LeadScrape';
    case 'emailsleuth':
      return 'Secrets services + Wizard campagne ou detail campagne';
    case 'hubspot':
      return 'Wizard campagne -> Enrichisseurs -> HubSpot';
    case 'bigquery':
      return 'Wizard campagne -> Enrichisseurs -> BigQuery';
  }
}

function getProviderUsageNote(providerId: EnrichmentProviderId): string {
  switch (providerId) {
    case 'csv':
      return "Le CSV est un enrichisseur immediat: tu l'actives quand tu veux injecter un lot manuel et laisser Dispatcher normaliser puis deduper les leads.";
    case 'leadscrape':
      return 'LeadScrape se pilote surtout depuis le wizard, car il depend de son ciblage dedie et de ses options de collecte.';
    case 'emailsleuth':
      return "EmailSleuth est ideal quand tu as deja un nom complet et un domaine, par exemple apres extraction depuis une liste de profils LinkedIn.";
    case 'hubspot':
      return "HubSpot agit surtout comme source de campagne. La page enrichisseurs sert ici de registre central pour savoir comment l'activer.";
    case 'bigquery':
      return "BigQuery agit surtout comme source de campagne. On le garde visible ici pour rendre le catalogue complet et coherent.";
  }
}

export default function EnrichersPage() {
  const [selectedProvider, setSelectedProvider] =
    useState<EnrichmentProviderId>('emailsleuth');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [mode, setMode] = useState<VerifyMode>('enhanced');

  const statusQuery = useQuery<EmailSleuthStatusResponse>({
    queryKey: ['emailsleuth', 'status'],
    queryFn: async () => {
      const { data } = await apiClient.get<EmailSleuthStatusResponse>(
        '/v1/enrichers/emailsleuth/status',
      );
      return data;
    },
    refetchInterval: 30_000,
  });

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<EmailSleuthVerifyResult>(
        '/v1/enrichers/emailsleuth/verify',
        {
          name,
          domain,
          mode,
        },
      );
      return data;
    },
    onSuccess: () => {
      toast.success('EmailSleuth lookup completed');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const isConfigured =
    statusQuery.data?.configured.base_url && statusQuery.data?.configured.api_key;
  const canVerify = Boolean(name.trim() && domain.trim() && isConfigured);
  const result = verifyMutation.data;
  const selectedProviderDefinition =
    ENRICHMENT_PROVIDERS.find((provider) => provider.id === selectedProvider) ??
    ENRICHMENT_PROVIDERS[0]!;

  function renderProviderBadge(providerId: EnrichmentProviderId) {
    if (providerId === 'emailsleuth') {
      if (statusQuery.isLoading) return <Badge tone="gray">Checking</Badge>;
      if (statusQuery.data?.connected) return <Badge tone="green">Connected</Badge>;
      if (statusQuery.data?.configured.base_url || statusQuery.data?.configured.api_key) {
        return <Badge tone="yellow">Partially configured</Badge>;
      }
      return <Badge tone="gray">Not configured</Badge>;
    }

    const definition = ENRICHMENT_PROVIDERS.find((provider) => provider.id === providerId);
    if (definition?.group === 'campaign_source') {
      return <Badge tone="blue">Campaign source</Badge>;
    }
    return <Badge tone="brand">Built-in</Badge>;
  }

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-6" data-testid="enrichers-page">
      <PageHeader
        title="Enrichisseurs"
        description="Un onglet par enrichisseur pour garder la page lisible, avec une config exploitable tout de suite pour EmailSleuth."
        action={
          <div className="flex items-center gap-2">
            <Link to="/settings/secrets">
              <Button variant="secondary">
                <ShieldCheck size={14} />
                Secrets services
              </Button>
            </Link>
            {selectedProvider === 'emailsleuth' ? (
              <Button
                variant="secondary"
                onClick={() => void statusQuery.refetch()}
                disabled={statusQuery.isFetching}
              >
                <RefreshCw size={14} />
                Refresh EmailSleuth
              </Button>
            ) : null}
          </div>
        }
      />

      <Tabs
        value={selectedProvider}
        onValueChange={(value) => setSelectedProvider(value as EnrichmentProviderId)}
        className="space-y-6"
      >
        <Card>
          <CardHeader
            title="Catalogue"
            subtitle="La meme base de providers alimente le wizard, le detail campagne, et cette vue globale."
          />
          <CardContent className="space-y-4">
            <TabsList className="overflow-x-auto px-1">
              {ENRICHMENT_PROVIDERS.map((provider) => {
                const Icon = provider.icon;

                return (
                  <TabsTrigger
                    key={provider.id}
                    value={provider.id}
                    className="shrink-0 whitespace-nowrap"
                    data-testid={`enricher-tab-${provider.id}`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Icon size={14} />
                      <span>{provider.label}</span>
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <div
              className="flex flex-col gap-3 rounded border border-gray-100 bg-gray-50 px-4 py-4 md:flex-row md:items-start md:justify-between"
              data-testid="enricher-summary"
            >
              <div>
                <div className="text-sm font-semibold text-brand-dark">
                  {selectedProviderDefinition.label}
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  {selectedProviderDefinition.description}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {renderProviderBadge(selectedProvider)}
                <Badge tone="gray">{selectedProviderDefinition.statusLabel}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <TabsContent value="emailsleuth">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(340px,0.9fr)]">
            <div className="space-y-6">
              <Card data-testid="emailsleuth-setup-card">
                <CardHeader
                  title="Configuration"
                  subtitle="Dispatcher dialogue avec EmailSleuth via deux secrets services."
                />
                <CardContent className="space-y-4">
                  <div className="rounded border border-gray-100 bg-gray-50 px-4 py-4 text-sm text-gray-700">
                    Ouvre <span className="font-medium text-brand-dark">Secrets services</span> et
                    ajoute deux entrees pour le service <code>emailsleuth</code>.
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded border border-gray-100 px-4 py-4">
                      <div className="text-xs uppercase text-gray-400">Secret 1</div>
                      <div className="mt-2 text-sm font-medium text-brand-dark">
                        service <code>emailsleuth</code>
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        kind <code>base_url</code>
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        valeur <code>{EMAILSLEUTH_BASE_URL_EXAMPLE}</code>
                      </div>
                    </div>
                    <div className="rounded border border-gray-100 px-4 py-4">
                      <div className="text-xs uppercase text-gray-400">Secret 2</div>
                      <div className="mt-2 text-sm font-medium text-brand-dark">
                        service <code>emailsleuth</code>
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        kind <code>api_key</code>
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        valeur = la meme que <code>API_KEY</code> dans ton service EmailSleuth
                      </div>
                    </div>
                  </div>

                  <div
                    className="rounded border border-dashed border-brand-border bg-brand-surface px-4 py-4 text-sm text-gray-700"
                    data-testid="emailsleuth-setup-note"
                  >
                    Le <code>base_url</code> doit pointer vers ton API EmailSleuth reachable par
                    Dispatcher. En local, le port par defaut du service est <code>8080</code>.
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm text-gray-600">
                      Des que les deux secrets sont presents, le status passe en connected et le
                      test manuel devient cliquable.
                    </div>
                    <Link to="/settings/secrets">
                      <Button variant="secondary">Configurer les secrets</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="emailsleuth-status-card">
                <CardHeader
                  title="EmailSleuth status"
                  subtitle="Dispatcher stocke la configuration dans les service secrets, puis sonde le service en lecture."
                />
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <Badge tone={statusQuery.data?.connected ? 'green' : 'yellow'}>
                      {statusQuery.data?.connected ? 'Connected' : 'Waiting for config'}
                    </Badge>
                    <Badge tone={statusQuery.data?.configured.base_url ? 'brand' : 'gray'}>
                      base_url {statusQuery.data?.configured.base_url ? 'ok' : 'missing'}
                    </Badge>
                    <Badge tone={statusQuery.data?.configured.api_key ? 'brand' : 'gray'}>
                      api_key {statusQuery.data?.configured.api_key ? 'ok' : 'missing'}
                    </Badge>
                    <Badge
                      tone={
                        statusQuery.data?.health?.auth_configured === true
                          ? 'green'
                          : statusQuery.data?.health?.auth_configured === false
                            ? 'yellow'
                            : 'gray'
                      }
                    >
                      upstream auth{' '}
                      {statusQuery.data?.health?.auth_configured === true
                        ? 'configured'
                        : statusQuery.data?.health?.auth_configured === false
                          ? 'missing'
                          : 'unknown'}
                    </Badge>
                  </div>

                  {statusQuery.isLoading ? (
                    <div className="rounded border border-dashed border-gray-200 px-4 py-8 text-sm text-gray-500">
                      Checking EmailSleuth...
                    </div>
                  ) : (
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded border border-gray-100 bg-gray-50 px-4 py-3">
                        <div className="text-xs uppercase text-gray-400">Service</div>
                        <div className="mt-1 text-sm font-medium text-brand-dark">
                          {statusQuery.data?.health?.service ?? 'emailsleuth'}
                        </div>
                      </div>
                      <div className="rounded border border-gray-100 bg-gray-50 px-4 py-3">
                        <div className="text-xs uppercase text-gray-400">Last healthcheck</div>
                        <div className="mt-1 text-sm font-medium text-brand-dark">
                          {formatDateTime(statusQuery.data?.health?.timestamp ?? null)}
                        </div>
                      </div>
                    </div>
                  )}

                  {statusQuery.data?.message && (
                    <div className="rounded border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                      {statusQuery.data.message}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader
                  title="Manual verify"
                  subtitle="Pratique pour tester une personne cible avant de brancher un batch entier."
                />
                <CardContent className="space-y-4">
                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
                    <div>
                      <Label htmlFor="emailsleuth-name">Full name</Label>
                      <Input
                        id="emailsleuth-name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="Tim Cook"
                        data-testid="emailsleuth-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="emailsleuth-mode">Mode</Label>
                      <Select
                        id="emailsleuth-mode"
                        value={mode}
                        onChange={(event) => setMode(event.target.value as VerifyMode)}
                        data-testid="emailsleuth-mode-select"
                      >
                        <option value="basic">basic</option>
                        <option value="enhanced">enhanced</option>
                        <option value="comprehensive">comprehensive</option>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="emailsleuth-domain">Company domain</Label>
                    <Input
                      id="emailsleuth-domain"
                      value={domain}
                      onChange={(event) => setDomain(event.target.value)}
                      placeholder="apple.com"
                      data-testid="emailsleuth-domain-input"
                    />
                  </div>

                  {!isConfigured && (
                    <div
                      className="rounded border border-brand-border bg-brand-surface px-4 py-3 text-sm text-gray-700"
                      data-testid="emailsleuth-config-warning"
                    >
                      Configure d'abord <code>emailsleuth/base_url</code> et{' '}
                      <code>emailsleuth/api_key</code> dans les secrets services.
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      variant="primary"
                      disabled={!canVerify || verifyMutation.isPending}
                      loading={verifyMutation.isPending}
                      onClick={() => verifyMutation.mutate()}
                      data-testid="emailsleuth-verify-submit"
                    >
                      <Binary size={14} />
                      Run verify
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader
                title="Latest result"
                subtitle="Resultat brut consolide par le proxy Dispatcher."
              />
              <CardContent className="space-y-4">
                {!result ? (
                  <div className="rounded border border-dashed border-gray-200 px-4 py-10 text-sm text-gray-500">
                    Lance un test manuel pour afficher le resultat ici.
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="emailsleuth-result">
                    <div className="rounded border border-gray-100 bg-gray-50 px-4 py-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="text-xs uppercase text-gray-400">
                            Selected email
                          </div>
                          <div className="mt-1 text-base font-semibold text-brand-dark">
                            {result.email ?? 'No email found'}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Badge tone={result.verified ? 'green' : 'yellow'}>
                            {result.verified ? 'Verified' : 'Unverified'}
                          </Badge>
                          <Badge tone="brand">
                            Confidence {formatConfidence(result.confidence)}
                          </Badge>
                          <Badge tone="gray">{result.duration_ms} ms</Badge>
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-gray-600">
                        {result.name} - {result.domain} - method {result.method ?? 'n/a'}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {result.candidates.length === 0 ? (
                        <div className="rounded border border-dashed border-gray-200 px-4 py-8 text-sm text-gray-500">
                          No candidate returned by EmailSleuth.
                        </div>
                      ) : (
                        result.candidates.map((candidate) => (
                          <div
                            key={candidate.email}
                            className="rounded border border-gray-100 px-4 py-3"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="text-sm font-medium text-brand-dark">
                                {candidate.email}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Badge tone={candidate.verified ? 'green' : 'gray'}>
                                  {candidate.verified ? 'verified' : 'not verified'}
                                </Badge>
                                <Badge tone="blue">{candidate.source}</Badge>
                                <Badge tone="gray">{Math.round(candidate.score * 100)}%</Badge>
                              </div>
                            </div>
                            {candidate.message && (
                              <div className="mt-2 text-xs text-gray-500">
                                {candidate.message}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    <div className="rounded border border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-600">
                      Methods used:{' '}
                      {result.debug?.methods_used?.length
                        ? result.debug.methods_used.join(', ')
                        : 'not exposed'}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {ENRICHMENT_PROVIDERS.filter((provider) => provider.id !== 'emailsleuth').map(
          (provider) => (
            <TabsContent key={provider.id} value={provider.id}>
              <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
                <Card>
                  <CardHeader
                    title="Usage"
                    subtitle="Ou et comment cet enrichisseur se branche dans Dispatcher."
                  />
                  <CardContent className="space-y-4">
                    <div className="rounded border border-gray-100 bg-gray-50 px-4 py-4 text-sm text-gray-700">
                      {provider.description}
                    </div>
                    <div className="rounded border border-dashed border-gray-200 px-4 py-4 text-sm text-gray-600">
                      {getProviderUsageNote(provider.id)}
                    </div>
                    <div className="rounded border border-gray-100 px-4 py-4">
                      <div className="text-xs uppercase text-gray-400">Activation</div>
                      <div className="mt-2 text-sm font-medium text-brand-dark">
                        {getProviderActivationPath(provider.id)}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader
                    title="Statut"
                    subtitle="Resume rapide de la place de ce provider dans le produit."
                  />
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {renderProviderBadge(provider.id)}
                      <Badge tone="gray">{provider.statusLabel}</Badge>
                    </div>
                    <div className="rounded border border-gray-100 bg-gray-50 px-4 py-4 text-sm text-gray-700">
                      {provider.group === 'campaign_source'
                        ? "Ce provider sert surtout a declarer qu'une campagne accepte une source de reprise ou d'import externe."
                        : 'Ce provider est directement exploitable depuis le wizard de campagne.'}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          ),
        )}
      </Tabs>
    </div>
  );
}
